import type { TFunction } from "i18next";

import createDockPageRegistry, { type DockPageMeta } from "~/components/dock/dockPageRegistry";

export interface RestockDockOptions {
	canAccessOrders: boolean;
	dashboardEnabled: boolean;
	kittingEnabled: boolean;
	ordersEnabled: boolean;
}

const createRestockPages = (t: TFunction<"common">): DockPageMeta[] => [
	{
		id: "dashboard",
		path: "/restock/dashboard",
		routePattern: "dashboard/*",
		title: t("dock.pages.restock.dashboard")
	},
	{
		id: "load-list",
		path: "/restock/load_list",
		routePattern: "load_list/*",
		title: t("dock.pages.restock.loadList")
	},
	{
		id: "orders",
		path: "/restock/orders",
		routePattern: "orders/*",
		title: t("dock.pages.restock.orders")
	},
	{
		id: "kit-builder",
		path: "/restock/orders/kitting",
		routePattern: "orders/*",
		title: t("dock.pages.restock.kitBuilder")
	},
	{
		id: "data-management-defaults",
		path: "/restock/data-management/defaults",
		routePattern: "data-management/*",
		title: t("dock.pages.restock.dataManagementDefaults")
	},
	{
		id: "data-management-items",
		path: "/restock/data-management/items",
		routePattern: "data-management/*",
		title: t("dock.pages.restock.dataManagementItems")
	},
	{
		id: "data-management-locations",
		path: "/restock/data-management/locations",
		routePattern: "data-management/*",
		title: t("dock.pages.restock.dataManagementLocations")
	},
	{
		id: "data-management-store-classes",
		path: "/restock/data-management/store-classes",
		routePattern: "data-management/*",
		title: t("dock.pages.restock.dataManagementStoreClasses")
	},
	{
		id: "data-management-delivery-schedules",
		path: "/restock/data-management/delivery-schedules",
		routePattern: "data-management/*",
		title: t("dock.pages.restock.dataManagementDeliverySchedules")
	},
	{
		id: "data-management-containers",
		path: "/restock/data-management/containers",
		routePattern: "data-management/*",
		title: t("dock.pages.restock.dataManagementContainers")
	},
	{
		id: "data-management-rules",
		path: "/restock/data-management/rules",
		routePattern: "data-management/*",
		title: t("dock.pages.restock.dataManagementRules")
	}
];

const DATA_MANAGEMENT_PAGE_IDS = new Set([
	"data-management-containers",
	"data-management-defaults",
	"data-management-delivery-schedules",
	"data-management-items",
	"data-management-locations",
	"data-management-rules",
	"data-management-store-classes"
]);

export const isRestockDockPageEnabled = (pageId: string, options: RestockDockOptions): boolean => {
	const ordersAccessible = options.ordersEnabled && options.canAccessOrders;
	if (pageId === "dashboard") {
		return options.dashboardEnabled;
	}
	if (pageId === "orders") {
		return ordersAccessible;
	}
	if (pageId === "kit-builder") {
		return ordersAccessible && options.kittingEnabled;
	}
	if (DATA_MANAGEMENT_PAGE_IDS.has(pageId)) {
		return options.ordersEnabled;
	}
	return true;
};

const createRestockDockPageRegistry = (t: TFunction<"common">, options: RestockDockOptions, defaultPageId: string) =>
	createDockPageRegistry(
		createRestockPages(t).filter((page) => isRestockDockPageEnabled(page.id, options)),
		defaultPageId
	);

export default createRestockDockPageRegistry;
